import type { ComponentType } from 'react';
import {
  Camera, UsersThree, TShirt, AirplaneTilt, MusicNotes, PaintBrush,
  ForkKnife, Car, Television, GameController, Barbell, BookOpen,
  Heart, House, PawPrint, Sparkle, Tag,
} from '../../../../design-system/icons';

interface InterestChipsProps {
  items: { label: string; val: number }[];
}

// Best-effort icon per interest name — the names themselves are free-form
// (Phyllo/InsightIQ), so this covers Figma's shown examples (node
// 2019:69388) plus other common ones; anything unmapped falls back to a
// generic tag icon rather than guessing.
const INTEREST_ICONS: Record<string, ComponentType<{ size?: number; className?: string }>> = {
  photography: Camera,
  friends: UsersThree,
  family: UsersThree,
  clothes: TShirt,
  fashion: TShirt,
  travel: AirplaneTilt,
  music: MusicNotes,
  art: PaintBrush,
  food: ForkKnife,
  cooking: ForkKnife,
  cars: Car,
  automotive: Car,
  tv: Television,
  television: Television,
  toys: GameController,
  gaming: GameController,
  fitness: Barbell,
  sports: Barbell,
  books: BookOpen,
  reading: BookOpen,
  beauty: Sparkle,
  health: Heart,
  wellness: Heart,
  home: House,
  pets: PawPrint,
};

function iconFor(label: string) {
  return INTEREST_ICONS[label.trim().toLowerCase()] ?? Tag;
}

/** Figma "Audience Fit — Chart 918" (node 2019:69388): interests as icon
 *  chips rather than a ranked bar list — the % is de-emphasized (it's a
 *  loose affinity score, not a precise share) in favor of scannable tags. */
export function InterestChips({ items }: InterestChipsProps) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => {
        const Icon = iconFor(item.label);
        return (
          <span
            key={item.label}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5"
          >
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-foreground">
              <Icon size={12} />
            </span>
            <span className="text-[12px] font-semibold text-foreground">{item.label}</span>
          </span>
        );
      })}
    </div>
  );
}
